import React, { useState, useEffect } from 'react';
import api from 'api';
import {
  Mask,
  Dropdown,
  Content,
  ContentWrapper,
  ButtonWrapper,
  Placeholder,
  CountryPill,
  Country,
  List,
  Label,
  Checkbox,
  Pills,
  DropdownIcon,
  SearchWrapper,
  SearchInput,
  CountryList,
  CountryWrapper,
  LanguageWrapper,
  Language,
  Lang,
} from './styles';

import { ReactComponent as CheckmarkIcon } from 'assets/icons/checkmark.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close-square.svg';

interface Props {
  onChange: (data: any) => void;
  value: CountryType[] | undefined;
}

export interface CountryType {
  country: string;
  code: string;
  languages: string[];
}

const CountryEditor = ({ onChange, value }: Props) => {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [selected, setSelected] = useState<CountryType[]>(
    value ? [...value] : []
  );
  const [filter, setFilter] = useState('');
  const [filteredResult, setFilteredResult] = useState([]);

  useEffect(() => {
    onChange(selected);
    // eslint-disable-next-line
  }, [selected]);

  useEffect(() => {
    const isFiltersApplied = filter.length > 0;
    const isFilterRemoved = filteredResult.length < availableCountries.length;

    if (isFiltersApplied || isFilterRemoved) {
      const filtered = [...availableCountries].filter(
        (country: CountryType) =>
          country.country.toUpperCase().indexOf(filter.toUpperCase()) > -1
      );

      setFilteredResult(filtered);
    } else {
      setFilteredResult(availableCountries);
    }
    // eslint-disable-next-line
  }, [filter]);

  useEffect(() => {
    api.resources.assistant.getAvailableCountryList().then(({ data }) => {
      setAvailableCountries(data);
      setFilteredResult(data);
    });

    onChange(selected);
    // eslint-disable-next-line
  }, []);

  const toggleCountry = (selectedCountry: CountryType) => {
    const selectedCountries = [...selected].map((country: CountryType) => ({
      ...country,
      languages: [...country.languages],
    }));

    const countryIndex = selectedCountries.findIndex(
      (country) => country.code === selectedCountry.code
    );

    const isSelected = countryIndex !== -1;

    if (isSelected) {
      selectedCountries.splice(countryIndex, 1);
    } else {
      selectedCountries.push(selectedCountry);
    }

    setSelected(selectedCountries);
  };

  const toggleLanguage = (country: CountryType, lang: string) => {
    const selectedCountries = [...selected].map((country: CountryType) => ({
      ...country,
      languages: [...country.languages],
    }));

    const countrySelected = selectedCountries.find(
      (countrySelected) => countrySelected.code === country.code
    );

    const isLangSelected = countrySelected?.languages.includes(lang);

    if (isLangSelected) {
      // If all languages are deseleted we removing country
      const isLastLanguageDeselected = countrySelected?.languages.length === 1;

      if (isLastLanguageDeselected) {
        const countryIndex = selectedCountries.findIndex(
          (countrySelected) => countrySelected.code === country.code
        );

        selectedCountries.splice(countryIndex, 1);
      } else {
        // Deselect only language
        const langIndex = countrySelected?.languages.indexOf(lang);

        countrySelected?.languages.splice(langIndex as number, 1);
      }
    } else {
      // Select Language
      countrySelected?.languages.push(lang);
    }

    setSelected(selectedCountries);
  };

  const isAnyCountrySelected = selected.length > 0;

  return (
    <>
      {!isEditorOpen ? (
        <button onClick={() => setIsEditorOpen(true)}>Edit Countries</button>
      ) : (
        <Content>
          {isOpen && <Mask onClick={() => setIsOpen(false)} />}
          <ContentWrapper>
            <Dropdown
              id="dropdownCountry"
              onClick={(e) => {
                if ((e.target as HTMLElement).id === 'dropdownCountry') {
                  setIsOpen(!isOpen);
                }
              }}
              isError={false}
            >
              {isAnyCountrySelected ? (
                <Pills>
                  {selected.map((country) => (
                    <CountryPill
                      key={country.code}
                      onClick={() => toggleCountry(country)}
                    >
                      {country.country}
                      <CloseIcon />
                    </CountryPill>
                  ))}
                </Pills>
              ) : (
                <Placeholder onClick={() => setIsOpen(!isOpen)}>
                  Please select countries
                </Placeholder>
              )}
              {isOpen && (
                <List>
                  <SearchWrapper>
                    <SearchIcon />
                    <SearchInput
                      type="text"
                      placeholder="Search country..."
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                    />
                  </SearchWrapper>
                  <CountryList>
                    {filteredResult.map((country: CountryType) => {
                      const isChecked = selected.find(
                        (countrySelected) =>
                          countrySelected.code === country.code
                      );

                      return (
                        <CountryWrapper key={country.code}>
                          <Label onClick={() => toggleCountry(country)}>
                            <Checkbox isChecked={!!isChecked}>
                              {isChecked && <CheckmarkIcon />}
                            </Checkbox>

                            <Country>{country.country}</Country>
                          </Label>
                          {isChecked && (
                            <LanguageWrapper>
                              {country.languages.map((lang: string) => {
                                const isLangSelected =
                                  isChecked.languages.includes(lang);

                                return (
                                  <Language
                                    key={`${country.code}-${lang}`}
                                    onClick={() =>
                                      toggleLanguage(country, lang)
                                    }
                                  >
                                    <Checkbox isChecked={!!isLangSelected}>
                                      {isLangSelected && <CheckmarkIcon />}
                                    </Checkbox>
                                    <Lang>{lang}</Lang>
                                  </Language>
                                );
                              })}
                            </LanguageWrapper>
                          )}
                        </CountryWrapper>
                      );
                    })}
                  </CountryList>
                </List>
              )}

              <DropdownIcon
                isOpen={isOpen}
                onClick={() => setIsOpen(!isOpen)}
              />
            </Dropdown>

            <ButtonWrapper>
              <button
                onClick={() => {
                  onChange(selected);
                  setIsEditorOpen(false);
                }}
              >
                Save
              </button>
              <button onClick={() => setIsEditorOpen(false)}>Cancel</button>
            </ButtonWrapper>
          </ContentWrapper>
        </Content>
      )}
    </>
  );
};

export default CountryEditor;
